import React, { useEffect, useState } from 'react';
import Header from './Header';
import API_URL from '../config';

function TabelasPrecosProdutosPage({ empresa, usuario, onVoltar, onSair, produtoSelecionado }) {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [pesquisa, setPesquisa] = useState('');
  const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, id: null });
  const [tabelas, setTabelas] = useState([]);
  const [precosPorTabela, setPrecosPorTabela] = useState([]);

  // Máscara monetária simples
  function formatarMascaraMonetaria(valor) {
    if (!valor) return '';
    let apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros.length < 3) {
      apenasNumeros = apenasNumeros.padStart(3, '0');
    }
    const int = apenasNumeros.slice(0, -2);
    const dec = apenasNumeros.slice(-2);
    return Number(int + '.' + dec).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function mascaraParaDecimal(valor) {
    if (!valor) return 0;
    let apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros.length < 3) {
      apenasNumeros = apenasNumeros.padStart(3, '0');
    }
    const int = apenasNumeros.slice(0, -2);
    const dec = apenasNumeros.slice(-2);
    return Number(int + '.' + dec);
  }

  const fetchRegistros = async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await fetch(`${API_URL}/tabelas_precos_produtos?empresa_id=${empresa.id}`);
      const data = await res.json();
      setRegistros(Array.isArray(data) ? data : []);
    } catch (err) {
      setErro('Erro ao buscar registros');
      setRegistros([]);
    }
    setLoading(false);
  };

  const fetchTabelas = async () => {
    try {
      const res = await fetch(`${API_URL}/tabelas_precos_full?empresa_id=${empresa.id}`);
      const data = await res.json();
      setTabelas(Array.isArray(data) ? data : []);
    } catch {
      setTabelas([]);
    }
  };

  useEffect(() => {
    fetchRegistros();
    fetchTabelas();
    // eslint-disable-next-line
  }, [empresa.id]);

  // Função para saber quais tabelas já estão cadastradas para o produto selecionado
  function tabelasNaoCadastradas() {
    if (!produtoSelecionado) return tabelas;
    const cadastradas = registros.filter(r => String(r.produto_id) === String(produtoSelecionado.id)).map(r => String(r.tabela_id));
    return tabelas.filter(t => !cadastradas.includes(String(t.id)));
  }

  const abrirModal = (param = null) => {
    if (param === 'edit') {
      // Edição em lote: abre todas as tabelas já cadastradas para o produto, preenchendo os preços
      const precos = registros
        .filter(r => String(r.produto_id) === String(produtoSelecionado?.id))
        .map(r => ({ tabela_id: r.tabela_id, nome: r.tabela_nome, preco: r.preco ? String(Math.round(Number(r.preco) * 100)) : '', ativo: r.ativo, id: r.id }));
      setPrecosPorTabela(precos);
    } else if (param && param.id) {
      // Edição individual: só o registro selecionado
      setPrecosPorTabela([
        { tabela_id: param.tabela_id, nome: param.tabela_nome, preco: param.preco ? String(Math.round(Number(param.preco) * 100)) : '', ativo: param.ativo, id: param.id }
      ]);
    } else {
      // Cadastro: só mostra as tabelas que ainda não têm registro para o produto
      const disponiveis = tabelasNaoCadastradas();
      setPrecosPorTabela(disponiveis.map(t => ({ tabela_id: t.id, nome: t.nome, preco: '', ativo: true })));
    }
    setErro('');
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setPrecosPorTabela([]);
    setErro('');
  };

  const handlePrecoTabelaChange = (tabela_id, valor) => {
    setPrecosPorTabela(prev => prev.map(p => p.tabela_id === tabela_id ? { ...p, preco: valor.replace(/\D/g, '') } : p));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setErro('');
    const registrosParaSalvar = precosPorTabela.filter(p => Number(p.preco) > 0);
    if (registrosParaSalvar.length === 0) {
      setErro('Digite ao menos um preço maior que zero!');
      return;
    }
    try {
      for (const p of registrosParaSalvar) {
        if (p.id) {
          // Update
          await fetch(`${API_URL}/tabelas_precos_produtos/${p.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              empresa_id: empresa.id,
              tabela_id: p.tabela_id,
              produto_id: produtoSelecionado ? produtoSelecionado.id : null,
              preco: mascaraParaDecimal(p.preco),
              ativo: p.ativo
            })
          });
        } else {
          // Insert
          await fetch(`${API_URL}/tabelas_precos_produtos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              empresa_id: empresa.id,
              tabela_id: p.tabela_id,
              produto_id: produtoSelecionado ? produtoSelecionado.id : null,
              preco: mascaraParaDecimal(p.preco),
              ativo: true
            })
          });
        }
      }
      fecharModal();
      fetchRegistros();
    } catch (err) {
      setErro('Erro ao salvar registros');
    }
  };

  const handleDelete = async id => {
    setConfirmarExclusao({ aberto: true, id });
  };

  const confirmarRemocao = async () => {
    try {
      await fetch(`${API_URL}/tabelas_precos_produtos/${confirmarExclusao.id}`, { method: 'DELETE' });
      fetchRegistros();
    } catch {
      setErro('Erro ao remover registro');
    }
    setConfirmarExclusao({ aberto: false, id: null });
  };

  const cancelarRemocao = () => {
    setConfirmarExclusao({ aberto: false, id: null });
  };

  const registrosFiltrados = Array.isArray(registros)
    ? registros.filter(r =>
        (produtoSelecionado ? String(r.produto_id) === String(produtoSelecionado.id) : true) &&
        ((r.tabela_nome || '').toLowerCase().includes(pesquisa.toLowerCase()) ||
         (r.produto_nome || '').toLowerCase().includes(pesquisa.toLowerCase()))
      )
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-blue-300">
      <Header usuario={usuario} empresa={empresa} onVoltar={onVoltar} onSair={onSair} />
      <div className="max-w-5xl mx-auto mt-10 bg-white rounded-lg shadow-lg p-6 relative">
        <h2 className="text-2xl font-bold text-blue-700 mb-6">Tabelas de Preços - Produtos</h2>
        <div className="mb-6 flex justify-end">
          <input
            type="text"
            value={pesquisa}
            onChange={e => setPesquisa(e.target.value)}
            placeholder="Pesquisar por tabela ou produto..."
            className="border rounded px-3 py-2 w-full max-w-xs"
          />
        </div>
        {erro && <div className="text-red-600 mb-4">{erro}</div>}
        {loading ? (
          <div>Carregando...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {registrosFiltrados.map(r => (
              <div key={r.id} className="bg-blue-50 rounded-lg shadow flex flex-col p-4 relative border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-blue-800 text-lg">{r.tabela_nome} - {r.produto_nome}</span>
                  <div className="flex space-x-2">
                    <button onClick={() => abrirModal(r)} title="Editar" className="text-blue-600 hover:text-blue-900">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 13l6-6 3 3-6 6H9v-3z" /></svg>
                    </button>
                    <button onClick={() => handleDelete(r.id)} title="Excluir" className="text-red-600 hover:text-red-900">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
                <div className="text-gray-700 mb-1"><b>Preço:</b> {Number(r.preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div className="text-gray-700 mb-1"><b>Ativo:</b> {r.ativo ? 'Sim' : 'Não'}</div>
                <div className="text-gray-700 mb-1"><b>ID:</b> {r.id}</div>
              </div>
            ))}
            {registrosFiltrados.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">Nenhum registro encontrado.</div>
            )}
          </div>
        )}
        <div className="flex justify-start gap-2 mt-6">
          <button
            onClick={() => abrirModal('edit')}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            disabled={registrosFiltrados.length === 0}
          >
            Editar Todos
          </button>
          <button
            onClick={() => abrirModal()}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
            disabled={tabelasNaoCadastradas().length === 0}
          >
            Adicionar
          </button>
        </div>
        {/* Modal */}
        {modalAberto && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md relative">
              <button onClick={fecharModal} className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl">&times;</button>
              <h3 className="text-xl font-bold text-blue-700 mb-4">Novo Registro</h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                {produtoSelecionado && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Produto</label>
                    <input type="text" value={produtoSelecionado.nome} className="border rounded px-3 py-2 w-full bg-gray-100" readOnly />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preços por Tabela*</label>
                  <div className="grid grid-cols-1 gap-2">
                    {precosPorTabela.map(p => (
                      <div key={p.tabela_id} className="flex items-center gap-2">
                        <span className="w-24 font-semibold">{p.nome}</span>
                        <input
                          type="text"
                          value={formatarMascaraMonetaria(p.preco)}
                          onChange={e => handlePrecoTabelaChange(p.tabela_id, e.target.value)}
                          className="border rounded px-3 py-2 w-32"
                          placeholder="0,00"
                          maxLength={12}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {erro && <div className="text-red-600 mb-2">{erro}</div>}
                <div className="flex justify-end space-x-2">
                  <button type="button" onClick={fecharModal} className="border border-blue-600 text-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition">Cancelar</button>
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition">Cadastrar</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Modal de confirmação de exclusão */}
        {confirmarExclusao.aberto && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm relative">
              <h3 className="text-lg font-bold text-red-700 mb-4">Confirmar exclusão?</h3>
              <p className="mb-4">Tem certeza que deseja remover este registro?</p>
              <div className="flex justify-end space-x-2">
                <button onClick={cancelarRemocao} className="border border-blue-600 text-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition">Cancelar</button>
                <button onClick={confirmarRemocao} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition">Remover</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TabelasPrecosProdutosPage; 